// pages/MusicAdminPage.js
import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import MusicSidebar from '../components/Music/MusicSideBar';
import ArtistForm from '../components/Music/Forms/ArtistForm';
import AlbumForm from '../components/Music/Forms/AlbumForm';
import SongForm from '../components/Music/Forms/SongForm';
import PlaylistForm from '../components/Music/Forms/PlaylistForm';
import GenreForm from '../components/Music/Forms/GenreForm';
import ArtistCard from '../components/Music/Cards/ArtistCard';
import AlbumCard from '../components/Music/Cards/AlbumCard';
import SongCard from '../components/Music/Cards/SongCard';
import PlaylistCard from '../components/Music/Cards/PlaylistCard';
import GenreCard from '../components/Music/Cards/GenreCard';
import QuickStatsPanel from '../components/Music/QuickStatsPanel';
import { PageHeader, ConfirmDialog } from '../components/admin';
import { 
  artistService, 
  albumService, 
  songService, 
  playlistService, 
  genreService
} from '../services/musicService';

function MusicAdminPage() {
  // State management
  const [activeTab, setActiveTab] = useState('songs'); // 'artists', 'albums', 'songs', 'playlists', 'genres'
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list' or 'table'
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [filters, setFilters] = useState({
    genre: '',
    year: '',
    artist: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  const getArtistId = (artist) => artist?.id ?? artist?.artist_id;
  const normalizeArtist = (artist) =>
    artist ? { ...artist, id: getArtistId(artist) } : artist;

  // Fetch data based on active tab
  useEffect(() => {
    fetchData();
  }, [activeTab, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      let response;
      const params = { ...filters, q: searchQuery };

      switch (activeTab) {
        case 'artists':
          response = await artistService.getAllArtists();
          break;
        case 'albums':
          response = await albumService.getAllAlbums(params);
          break;
        case 'songs':
          response = await songService.getAllSongs(params);
          break;
        case 'playlists':
          response = await playlistService.getAllPlaylists(params);
          break;
        case 'genres':
          response = await genreService.getAllGenres();
          break;
        default:
          response = await songService.getAllSongs(params);
      }

      const fetchedData = response.data?.data || response.data || [];
      setData(
        activeTab === 'artists'
          ? fetchedData.map(normalizeArtist)
          : fetchedData
      );
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // CRUD Operations
  const handleCreate = async (itemData) => {
    try {
      let response;
      
      switch (activeTab) {
        case 'artists':
          response = await artistService.createArtist(itemData);
          break;
        case 'albums':
          response = await albumService.createAlbum(itemData);
          break;
        case 'songs':
          response = await songService.createSong(itemData);
          break;
        case 'playlists':
          response = await playlistService.createPlaylist(itemData);
          break;
        case 'genres':
          response = await genreService.createGenre(itemData);
          break;
      }

      if (response.data.success) {
        const createdItem = activeTab === 'artists'
          ? normalizeArtist(response.data.data)
          : response.data.data;
        setData(prev => [createdItem, ...prev]);
        setShowForm(false);
        setEditingItem(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create item');
    }
  };

  const handleUpdate = async (itemData) => {
    try {
      let response;
      
      switch (activeTab) {
        case 'artists':
          response = await artistService.updateArtist(getArtistId(itemData), itemData);
          break;
        case 'albums':
          response = await albumService.updateAlbum(itemData.id, itemData);
          break;
        case 'songs':
          response = await songService.updateSong(itemData.id, itemData);
          break;
        case 'playlists':
          response = await playlistService.updatePlaylist(itemData.id, itemData);
          break;
        case 'genres':
          response = await genreService.updateGenre(itemData.id, itemData);
          break;
      }

      if (response.data.success) {
        const updatedItem = activeTab === 'artists'
          ? normalizeArtist(response.data.data)
          : response.data.data;
        setData(prev => prev.map(item => 
          item.id === getArtistId(itemData) ? updatedItem : item
        ));
        setEditingItem(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update item');
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    const id = itemToDelete.id;

    try {
      switch (activeTab) {
        case 'artists':
          await artistService.deleteArtist(id);
          break;
        case 'albums':
          await albumService.deleteAlbum(id);
          break;
        case 'songs':
          await songService.deleteSong(id);
          break;
        case 'playlists':
          await playlistService.deletePlaylist(id);
          break;
        case 'genres':
          await genreService.deleteGenre(id);
          break;
      }

      setData(prev => prev.filter(item => item.id !== id));
      setSelectedItems(prev => prev.filter(itemId => itemId !== id));
      setItemToDelete(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete item');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    setIsBulkDeleting(true);

    try {
      const deleteItem = {
        artists: artistService.deleteArtist,
        albums: albumService.deleteAlbum,
        songs: songService.deleteSong,
        playlists: playlistService.deletePlaylist,
        genres: genreService.deleteGenre,
      }[activeTab];

      await Promise.all(selectedItems.map((id) => deleteItem(id)));
      setData((currentData) =>
        currentData.filter((item) => !selectedItems.includes(item.id))
      );
      setSelectedItems([]);
      setShowBulkModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete selected items');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === data.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(data.map(item => item.id));
    }
  };

  // Render appropriate form based on active tab
  const renderForm = () => {
    const formProps = {
      onSubmit: editingItem ? handleUpdate : handleCreate,
      onCancel: () => {
        setShowForm(false);
        setEditingItem(null);
      }
    };

    switch (activeTab) {
      case 'artists':
        return (
          <ArtistForm 
            {...formProps}
            initialData={editingItem}
            title={editingItem ? 'Edit Artist' : 'Add New Artist'}
          />
        );
      case 'albums':
        return (
          <AlbumForm 
            {...formProps}
            initialData={editingItem}
            title={editingItem ? 'Edit Album' : 'Add New Album'}
          />
        );
      case 'songs':
        return (
          <SongForm 
            {...formProps}
            initialData={editingItem}
            title={editingItem ? 'Edit Song' : 'Add New Song'}
          />
        );
      case 'playlists':
        return (
          <PlaylistForm 
            {...formProps}
            initialData={editingItem}
            title={editingItem ? 'Edit Playlist' : 'Add New Playlist'}
          />
        );
      case 'genres':
        return (
          <GenreForm 
            {...formProps}
            initialData={editingItem}
            title={editingItem ? 'Edit Genre' : 'Add New Genre'}
          />
        );
      default:
        return null;
    }
  };

  // Render appropriate card based on active tab
  const renderItem = (item) => {
    const itemId = item.id ?? item.artist_id;
    const commonProps = {
      key: itemId,
      item,
      isSelected: selectedItems.includes(itemId),
      onSelect: () => handleSelectItem(itemId),
      onEdit: () => {
        setEditingItem(item);
        setShowForm(true);
      },
      onDelete: () => setItemToDelete(item),
      viewMode
    };

    switch (activeTab) {
      case 'artists':
        return <ArtistCard {...commonProps} />;
      case 'albums':
        return <AlbumCard {...commonProps} />;
      case 'songs':
        return <SongCard {...commonProps} />;
      case 'playlists':
        return <PlaylistCard {...commonProps} />;
      case 'genres':
        return <GenreCard {...commonProps} />;
      default:
        return null;
    }
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 'artists': return 'Artists';
      case 'albums': return 'Albums';
      case 'songs': return 'Songs';
      case 'playlists': return 'Playlists';
      case 'genres': return 'Genres';
      default: return 'Music Library';
    }
  };

  if (loading && !data.length) return <LoadingSpinner />;
  if (error) return <ErrorDisplay error={error} onRetry={fetchData} />;

  return (
    <div className="space-y-4">
      {/* Header */}
      {itemToDelete && (
        <ConfirmDialog
          title={`Delete ${activeTab.slice(0, -1)}`}
          message={`Delete “${itemToDelete.name || itemToDelete.title || 'this item'}”? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setItemToDelete(null)}
        />
      )}

      {showBulkModal && (
        <ConfirmDialog
          title="Delete selected items"
          message={`Delete ${selectedItems.length} selected items? This action cannot be undone.`}
          confirmLabel={`Delete ${selectedItems.length} items`}
          onConfirm={handleBulkDelete}
          onCancel={() => setShowBulkModal(false)}
          busy={isBulkDeleting}
        />
      )}

      <PageHeader
        title="Music"
        description="Manage your music library"
        actions={
          <>
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : viewMode === 'list' ? 'table' : 'grid')}
              className="btn-secondary"
            >
              {viewMode === 'grid' ? 'Grid' : viewMode === 'list' ? 'List' : 'Table'}
              <svg className="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {viewMode === 'grid' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                ) : viewMode === 'list' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 18h18M3 6h18" />
                )}
              </svg>
            </button>
            <button
              onClick={() => {
                setShowForm(true);
                setEditingItem(null);
              }}
              className="btn-primary"
            >
              Add {getTabTitle().slice(0, -1)}
            </button>
          </>
        }
      />
        <div className="flex flex-col lg:flex-row gap-5">
          {/* Sidebar */}
          <div className="lg:w-60 shrink-0">
            <MusicSidebar 
              activeTab={activeTab}
              onTabChange={setActiveTab}
              stats={stats}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Quick Stats Panel */}
            <QuickStatsPanel 
              activeTab={activeTab}
              data={data}
              selectedCount={selectedItems.length}
              onRefresh={fetchData}
              onBulkDelete={handleBulkDelete}
              onSelectAll={handleSelectAll}
              isAllSelected={selectedItems.length === data.length && data.length > 0}
            />

            {/* Filters */}
            <div className="mb-6 panel p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <span className="text-gray-400 text-sm">Filter by:</span>
                
                {activeTab === 'songs' && (
                  <>
                    <select 
                      value={filters.genre}
                      onChange={(e) => setFilters(prev => ({ ...prev, genre: e.target.value }))}
                      className="field !w-auto !py-2"
                    >
                      <option value="">All Genres</option>
                      <option value="rock">Rock</option>
                      <option value="pop">Pop</option>
                      <option value="jazz">Jazz</option>
                      <option value="hiphop">Hip Hop</option>
                      <option value="electronic">Electronic</option>
                    </select>
                    
                    <select 
                      value={filters.year}
                      onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                      className="field !w-auto !py-2"
                    >
                      <option value="">All Years</option>
                      {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </>
                )}
                
                <select 
                  value={filters.sortBy}
                  onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                  className="field !w-auto !py-2"
                >
                  <option value="created_at">Date Added</option>
                  <option value="name">Name</option>
                  {activeTab === 'songs' && <option value="play_count">Plays</option>}
                  {activeTab === 'albums' && <option value="release_year">Year</option>}
                </select>
                
                <button
                  onClick={() => setFilters(prev => ({ 
                    ...prev, 
                    sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' 
                  }))}
                  className="btn-secondary !py-2"
                >
                  {filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  <svg className={`w-4 h-4 transform ${filters.sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={() => setFilters({
                    genre: '',
                    year: '',
                    artist: '',
                    sortBy: 'created_at',
                    sortOrder: 'desc'
                  })}
                  className="btn-ghost !py-2"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Form or Content */}
            {showForm || editingItem ? (
              <div className="max-w-2xl mx-auto">
                {renderForm()}
              </div>
            ) : (
              <>
                {data.length === 0 ? (
                  <div className="card py-16 flex flex-col items-center text-center">
                    <h3 className="text-lg font-semibold text-white">No {getTabTitle()} Yet</h3>
                    <p className="text-sm text-gray-500 mt-1">Add your first {getTabTitle().slice(0, -1).toLowerCase()} to get started</p>
                    <button
                      onClick={() => setShowForm(true)}
                      className="btn-primary mt-5"
                    >
                      Add First {getTabTitle().slice(0, -1)}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Bulk Actions Bar */}
                    {selectedItems.length > 0 && (
                      <div className="mb-6 p-4 bg-accent-dim border border-accent/30 rounded-xl flex items-center justify-between">
                        <div className="text-accent-soft">
                          <span className="font-semibold">{selectedItems.length}</span> items selected
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedItems([])}
                            className="btn-ghost !py-1.5 !px-3"
                          >
                            Clear Selection
                          </button>
                          <button
                            onClick={() => setShowBulkModal(true)}
                            className="btn-danger !py-1.5 !px-3"
                          >
                            Delete Selected
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Data Grid */}
                    <div className={
                      viewMode === 'grid' 
                        ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                        : viewMode === 'list'
                        ? 'space-y-4'
                        : 'overflow-x-auto'
                    }>
                      {                      viewMode === 'table' ? (
                        <div className="card overflow-hidden">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="table-head">
                              <th className="px-5 py-3 text-left">
                                <input 
                                  type="checkbox"
                                  checked={selectedItems.length === data.length && data.length > 0}
                                  onChange={handleSelectAll}
                                  className="rounded border-gray-600"
                                />
                              </th>
                              <th className="px-5 py-3 text-left">Name</th>
                              {activeTab === 'artists' && <th className="px-5 py-3 text-left">Country</th>}
                              {activeTab === 'albums' && <th className="px-5 py-3 text-left">Artist</th>}
                              {activeTab === 'songs' && (
                                <>
                                  <th className="px-5 py-3 text-left">Artist</th>
                                  <th className="px-5 py-3 text-left">Duration</th>
                                  <th className="px-5 py-3 text-left">Plays</th>
                                </>
                              )}
                              {activeTab === 'playlists' && <th className="px-5 py-3 text-left">Visibility</th>}
                              <th className="px-5 py-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.map(item => (
                              <tr key={item.id} className="table-row">
                                <td className="px-5 py-3.5">
                                  <input 
                                    type="checkbox"
                                    checked={selectedItems.includes(item.id)}
                                    onChange={() => handleSelectItem(item.id)}
                                    className="rounded border-gray-600"
                                  />
                                </td>
                                <td className="px-5 py-3.5">
                                  <div className="font-medium text-white">{item.name || item.title}</div>
                                </td>
                                {/* Additional columns based on tab */}
                                <td className="px-5 py-3.5">
                                  <div className="flex gap-2 justify-end">
                                    <button
                                      onClick={() => {
                                        setEditingItem(item);
                                        setShowForm(true);
                                      }}
                                      className="btn-ghost !py-1.5 !px-3"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => setItemToDelete(item)}
                                      className="btn-danger !py-1.5 !px-3"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      ) : (
                        data.map(renderItem)
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
    </div>
  );
}

export default MusicAdminPage;